"use client";

import { useEffect, useState } from "react";
import { AutoRetry } from "@/components/error/AutoRetry";

/**
 * Brand domain error boundary.
 * 
 * Detects Convex service errors and shows a maintenance-style page with auto-retry.
 * For other errors, shows a generic "Something went wrong" message.
 */

function isServiceError(error: Error): boolean {
    const msg = (error.message || "").toLowerCase();
    // Only match genuine network-level failures where the server is unreachable
    // Do NOT match broad terms like "network", "fetch failed", "internal server error"
    // because those can appear in normal query errors for non-existent data
    return (
        msg.includes("econnrefused") ||
        msg.includes("econnreset") ||
        msg.includes("socket hang up") ||
        msg.includes("dns resolution failed")
    );
}



export default function BrandDomainError({
    error,
    reset,
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    const isOutage = isServiceError(error);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        console.error("[BrandDomainError] Error:", error);
    }, [error]);

    // ── Service outage UI ────────────────────────────────────────────────────
    if (isOutage) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(160deg, #1a0a14 0%, #120812 40%, #0e0a12 100%)",
                    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
                    padding: "24px",
                    position: "relative",
                    overflow: "hidden",
                    color: "#e2e8f0",
                }}
            >
                {/* Background glow */}
                <div
                    style={{
                        position: "absolute",
                        top: "20%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "700px",
                        height: "500px",
                        borderRadius: "50%",
                        background: "rgba(232,121,160,0.12)",
                        filter: "blur(100px)",
                        pointerEvents: "none",
                    }}
                />

                {/* Card */}
                <div
                    style={{
                        position: "relative",
                        maxWidth: "520px",
                        width: "100%",
                        background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: "20px",
                        padding: "48px 40px 36px",
                        backdropFilter: "blur(16px)",
                        textAlign: "center",
                        boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
                    }}
                >
                    {/* Shield Icon */}
                    <div style={{ marginBottom: "24px", display: "flex", justifyContent: "center" }}>
                        <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                            <path d="M28 4L8 14V26C8 39.2 16.4 51.4 28 54C39.6 51.4 48 39.2 48 26V14L28 4Z" stroke="#e879a0" strokeWidth="2.5" fill="#e879a0" fillOpacity="0.08" />
                            <path d="M33.5 22.5C33.5 20.84 32.66 19.37 31.38 18.5L33 16.88L31.12 15L29.5 16.62C28.63 16.22 27.65 16 26.62 16C22.78 16 19.62 19.16 19.62 23C19.62 26.84 22.78 30 26.62 30C27.65 30 28.63 29.78 29.5 29.38L36 35.88L37.88 34L31.38 27.5C32.66 26.63 33.5 25.16 33.5 23.5V22.5Z" fill="#e879a0" opacity="0.9" />
                            <circle cx="26.5" cy="23" r="3" stroke="#e879a0" strokeWidth="1.5" fill="none" />
                        </svg>
                    </div>

                    <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#e879a0", marginBottom: "12px" }}>
                        Temporary System Maintenance
                    </h1>

                    <p style={{ fontSize: "14px", color: "#94a3b8", lineHeight: 1.7, marginBottom: "8px" }}>
                        We&apos;re experiencing a brief service interruption. Our team is working on it and we&apos;ll be back very soon.
                    </p>
                    <p style={{ fontSize: "12px", color: "#64748b", lineHeight: 1.6, marginBottom: "28px" }}>
                        Your store and all your data are completely safe. This page will automatically retry.
                    </p>

                    {/* Auto-retry progress bar */}
                    <AutoRetry duration={5} theme="brand" />

                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            width: "100%",
                            padding: "13px 24px",
                            borderRadius: "10px",
                            background: "#e879a0",
                            color: "#fff",
                            fontWeight: 600,
                            fontSize: "14px",
                            border: "none",
                            cursor: "pointer",
                            marginBottom: "12px",
                            boxShadow: "0 4px 20px rgba(232,121,160,0.25)",
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M13.65 2.35A7.96 7.96 0 0 0 8 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14 6 6 0 1 1 8 2c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z" fill="white" />
                        </svg>
                        Reload App
                    </button>

                    <button
                        onClick={() => window.open("https://wa.me/256780063058?text=Hi%2C%20I%20am%20experiencing%20issues%20accessing%20my%20store.", "_blank")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            padding: "11px 24px",
                            borderRadius: "10px",
                            background: "transparent",
                            color: "#94a3b8",
                            fontWeight: 500,
                            fontSize: "13px",
                            border: "1px solid rgba(255,255,255,0.08)",
                            cursor: "pointer",
                            width: "100%",
                            marginBottom: "12px",
                        }}
                    >
                        Contact Support
                    </button>

                    {/* View Technical Details */}
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#475569",
                            fontSize: "12px",
                            cursor: "pointer",
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                        }}
                    >
                        {showDetails ? "Hide" : "View"} Technical Details
                    </button>

                    {showDetails && (
                        <div
                            style={{
                                marginTop: "12px",
                                padding: "12px",
                                borderRadius: "8px",
                                background: "rgba(0,0,0,0.3)",
                                textAlign: "left",
                                fontSize: "11px",
                                fontFamily: "monospace",
                                color: "#64748b",
                                wordBreak: "break-all",
                            }}
                        >
                            <div>Error: {error.message}</div>
                            {error.digest && <div>Digest: {error.digest}</div>}
                            <div>Time: {new Date().toISOString()}</div>
                        </div>
                    )}
                </div>

                <div style={{ maxWidth: "520px", width: "100%", display: "flex", justifyContent: "space-between", marginTop: "24px", padding: "0 8px", fontSize: "11px", color: "#334155", fontFamily: "monospace" }}>
                    <span>Kuttos Technologies</span>
                    <span>Status: Monitoring</span>
                </div>

                {/* Animated dots */}
                <div style={{ marginTop: "16px", display: "flex", gap: "6px" }}>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                background: "#e879a0",
                                animation: `pulse-dot 1.4s ease-in-out ${i * 0.2}s infinite`,
                            }}
                        />
                    ))}
                </div>

                <style>{`
                    * { box-sizing: border-box; margin: 0; padding: 0; }
                    @keyframes pulse-dot {
                        0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                        40% { opacity: 1; transform: scale(1.2); }
                    }
                `}</style>
            </div>
        );
    }

    // ── Generic error UI ─────────────────────────────────────────────────────
    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(160deg, #0a0a1a 0%, #0d0d1f 40%, #0a0e1a 100%)",
                fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
                padding: "24px",
                position: "relative",
                overflow: "hidden",
                color: "#e2e8f0",
            }}
        >
            {/* Background glow */}
            <div
                style={{
                    position: "absolute",
                    top: "20%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "600px",
                    height: "400px",
                    borderRadius: "50%",
                    background: "rgba(245,158,11,0.08)",
                    filter: "blur(120px)",
                    pointerEvents: "none",
                }}
            />

            <div
                style={{
                    position: "relative",
                    maxWidth: "520px",
                    width: "100%",
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: "20px",
                    padding: "48px 40px 36px",
                    backdropFilter: "blur(16px)",
                    textAlign: "center",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
                }}
            >
                {/* Warning Icon */}
                <div style={{ marginBottom: "24px", display: "flex", justifyContent: "center" }}>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                        <path d="M28 6L4 50h48L28 6z" stroke="#f59e0b" strokeWidth="2.5" fill="#f59e0b" fillOpacity="0.08" />
                        <path d="M28 22v12" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
                        <circle cx="28" cy="40" r="2" fill="#f59e0b" />
                    </svg>
                </div>

                <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#fbbf24", marginBottom: "12px" }}>
                    Something Went Wrong
                </h1>
                <p style={{ fontSize: "14px", color: "#94a3b8", lineHeight: 1.7, marginBottom: "28px" }}>
                    This page encountered an unexpected error. Try reloading or contact support if the problem persists.
                </p>

                <AutoRetry duration={5} theme="amber" />

                <button
                    onClick={() => window.location.reload()}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        width: "100%",
                        padding: "13px 24px",
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #f59e0b, #d97706)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        border: "none",
                        cursor: "pointer",
                        marginBottom: "12px",
                        boxShadow: "0 4px 20px rgba(245,158,11,0.25)",
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.65 2.35A7.96 7.96 0 0 0 8 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14 6 6 0 1 1 8 2c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z" fill="white" />
                    </svg>
                    Try Again
                </button>

                <button
                    onClick={() => (window.location.href = "/")}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        padding: "11px 24px",
                        borderRadius: "10px",
                        background: "transparent",
                        color: "#94a3b8",
                        fontWeight: 500,
                        fontSize: "13px",
                        border: "1px solid rgba(255,255,255,0.08)",
                        cursor: "pointer",
                        width: "100%",
                    }}
                >
                    Go Home
                </button>
            </div>

            <style>{`* { box-sizing: border-box; margin: 0; padding: 0; }`}</style>
        </div>
    );
}
